import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UserPlus, KeyRound } from "lucide-react";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "caller", client: "" });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setCreateOpen(false);
      setForm({ name: "", email: "", password: "", role: "caller", client: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/users/${id}`, body);
      toast.success("User updated");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const resetPassword = async () => {
    if (!newPassword) return;
    await patch(resetUser.id, { password: newPassword });
    setResetUser(null);
    setNewPassword("");
  };

  const assignableRoles = ROLES.filter((r) => r.id !== "master_admin" || me?.role === "master_admin");

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-[#111827] tracking-tight">Team Users</h1>
          <p className="text-sm text-[#4B5563] mt-1">Create accounts and assign roles. Field permissions per role are managed in Permissions.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="create-user-button">
          <UserPlus className="h-4 w-4 mr-2" /> Add User
        </Button>
      </div>

      <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              {["Name", "Email", "Client", "Role", "Active", "Actions"].map((h) => (
                <th key={h} className="text-left text-[10px] font-bold uppercase tracking-widest text-[#4B5563] py-2 px-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[#F3F4F6] hover:bg-[#F3F4F6] transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="py-2 px-3 font-medium">{u.name}</td>
                <td className="py-2 px-3 text-[#4B5563]">{u.email}</td>
                <td className="py-2 px-3 text-[#4B5563]">{u.client || "—"}</td>
                <td className="py-2 px-3">
                  <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })}
                    disabled={u.role === "master_admin" && me?.role !== "master_admin"}>
                    <SelectTrigger className="rounded-sm h-8 w-48" data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {assignableRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-2 px-3">
                  <Switch checked={u.active} onCheckedChange={(v) => patch(u.id, { active: v })}
                    disabled={u.id === me?.id} data-testid={`user-active-switch-${u.id}`} />
                </td>
                <td className="py-2 px-3">
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setResetUser(u)} data-testid={`user-reset-password-${u.id}`}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset Password
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="font-heading font-extrabold">Add Team User</DialogTitle></DialogHeader>
          <form onSubmit={createUser} className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Full Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="new-user-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" data-testid="new-user-email-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Password</Label>
              <Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm" data-testid="new-user-password-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assignableRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Client (only for Client role — restricts them to that client's cases)</Label>
              <Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}
                className="mt-1 rounded-sm" placeholder="e.g. HDFC BANK" data-testid="new-user-client-input" />
            </div>
            <Button type="submit" className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="create-user-submit">Create User</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={() => setResetUser(null)}>
        <DialogContent className="rounded-sm" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="font-heading font-extrabold">Reset Password — {resetUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="password" minLength={6} placeholder="New password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} className="rounded-sm" data-testid="reset-password-input" />
            <Button onClick={resetPassword} className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="reset-password-submit">Set New Password</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
